// Office dashboard -- the "launchpad" (Session ADC). Every widget is a REGISTERED BLOCK
// (src/lib/dashboardLayout.ts) rendered from the office's arranged layout: the ONE shared
// per-tenant layout persisted server-side (TenantConfig.dashboardLayout, GET/PUT/DELETE
// /api/dashboard/layout -- never browser storage). No saved layout = the default order below,
// which is the pre-ADC dashboard exactly.
//
// Tiles mirror the Master Sheet, fed entirely from live Postgres -- no static values anywhere;
// a tile with nothing to show reads 0 or £0, never "--". (Session AAK: the sites table was
// removed from the dashboard -- it duplicated the dedicated Sites page at /office/sites, which
// owns the full sortable, filterable, searchable list.)
//
// Plain English, zero jargon -- the office manager lives here. Each tile carries
// a one-line hint so the figure is unambiguous (the window it covers).
import { useEffect, useRef, useState } from "react";
import type { DragEvent as ReactDragEvent, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, useSortable, rectSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "../../lib/api";
import type { WorksClassification } from "../../lib/api";
import {
  DASHBOARD_BLOCKS,
  applyDashboardLayout,
  applyBlockDragEnd,
  defaultDashboardLayout,
  dashboardLayoutToStore,
  navItemByPath,
  pinnableNavSections,
  type DashboardBlockKey,
  type DashboardBlockPlacement,
  type ResolvedDashboardLayout,
} from "../../lib/dashboardLayout";
import { PIN_DRAG_TYPE, beginPinDrag, currentPinDrag, endPinDrag, pinDropDecision, usePinDrag } from "../../lib/pinDrag";
import { ConfirmButton } from "../../components/ConfirmButton";
import { Button } from "../../components/Button";
import { Loader } from "../../components/Loader";
import { PageHeader } from "../../components/PageHeader";
import { StatusPill } from "../../components/StatusPill";
import { LogCallModal } from "../../components/LogCallModal";
import { OverflowMenu, overflowItemCls } from "../../components/OverflowMenu";
import { KpiTileSet } from "./KpiTileSet";
import { AddServiceRecordModal } from "./AddServiceRecordModal";
import { SendRfidTagModal } from "./SendRfidTagModal";
import { NewProjectModal } from "./ProjectsPage";
import { OutstandingJobsPanel } from "./OutstandingJobsPanel";
import { ActiveQuotesPanel } from "./ActiveQuotesPanel";
import { StandbyPanel } from "./StandbyPanel";
import { OfficeNoticesPanel } from "./OfficeNoticesPanel";

// The tile wording helpers moved with the tiles to KpiTileSet.tsx (Session SC7B, so the dashboard and Priorities share
// one set of tiles). They are re-exported unchanged: their tests and any importer still find them here.
export {
  entrapmentHint,
  renewalCyclesHint,
  subcontractorPaymentsAttention,
  subcontractorPaymentsHint,
  subcontractorPaymentsTone,
  xeroBillingTileContent,
  emailEngineTileContent,
  dataRetentionTileContent,
} from "./KpiTileSet";
export type { SubcontractorPaymentCounts } from "./KpiTileSet";

export function OfficeDashboard() {
  // The office's arranged layout (block order + visibility + pinned shortcuts). Loaded once;
  // null while loading. A load failure falls back to the default layout silently -- the
  // dashboard itself must never be blocked by its own arrangement record.
  const [layout, setLayout] = useState<ResolvedDashboardLayout | null>(null);
  // Customise mode (Session ADC): non-null = the office is arranging the dashboard. All edits
  // (show/hide, pins, order) accumulate on this local draft and are saved in ONE PUT when the
  // office presses Done ("exit to save"). Reset to default is the exception: it calls the
  // DELETE (the layout's delete path) immediately.
  const [draft, setDraft] = useState<ResolvedDashboardLayout | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const customising = draft !== null;
  // A small drag distance before activating (the nav-sidebar precedent), so a click on a frame
  // control is never read as a drag.
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Publish customise mode to the office shell (Session AEA) so the sidebar's nav links become
  // pin-drag sources for exactly as long as the office is arranging. The unmount cleanup means
  // navigating away mid-customise can never leave the sidebar in drag mode.
  const { setCustomising } = usePinDrag();
  useEffect(() => {
    setCustomising(customising);
    return () => setCustomising(false);
  }, [customising, setCustomising]);

  useEffect(() => {
    let live = true;
    api.dashboard
      .layout()
      .then(({ layout: saved }) => {
        if (live) setLayout(applyDashboardLayout(saved));
      })
      .catch(() => {
        if (live) setLayout(defaultDashboardLayout());
      });
    return () => {
      live = false;
    };
  }, []);

  function startCustomising() {
    if (!layout) return;
    setSaveError(false);
    setDraft({ blocks: layout.blocks.map((b) => ({ ...b })), pins: [...layout.pins] });
  }

  function toggleBlockVisible(key: DashboardBlockKey) {
    setDraft((d) => d && { ...d, blocks: d.blocks.map((b) => (b.key === key ? { ...b, visible: !b.visible } : b)) });
  }

  function toggleBlockSpan(key: DashboardBlockKey) {
    setDraft(
      (d) =>
        d && { ...d, blocks: d.blocks.map((b) => (b.key === key ? { ...b, span: b.span === "full" ? "half" : "full" } : b)) },
    );
  }

  // A drop re-orders the draft via the pure applyBlockDragEnd (dnd-kit ids are block keys).
  function onBlockDragEnd(e: DragEndEvent) {
    const { active, over } = e;
    if (!over) return;
    setDraft((d) => {
      if (!d) return d;
      const { blocks, changed } = applyBlockDragEnd(d.blocks, String(active.id), String(over.id));
      return changed ? { ...d, blocks } : d;
    });
  }

  function addPin(to: string) {
    setDraft((d) => (d && !d.pins.includes(to) ? { ...d, pins: [...d.pins, to] } : d));
  }

  function removePin(to: string) {
    setDraft((d) => d && { ...d, pins: d.pins.filter((p) => p !== to) });
  }

  // Done = save the draft in one PUT, then leave customise mode. A failure keeps the draft (and
  // the mode) so nothing the office arranged is lost.
  async function saveDraft() {
    if (!draft || saving) return;
    setSaving(true);
    setSaveError(false);
    try {
      const { layout: stored } = await api.dashboard.setLayout(dashboardLayoutToStore(draft));
      setLayout(applyDashboardLayout(stored));
      setDraft(null);
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  // Reset to default: the layout's DELETE. Applies immediately (the stored record is removed),
  // and stays in customise mode with the default draft so the office can carry on arranging.
  async function resetToDefault() {
    if (saving) return;
    setSaving(true);
    setSaveError(false);
    try {
      await api.dashboard.resetLayout();
      setLayout(defaultDashboardLayout());
      setDraft(defaultDashboardLayout());
    } catch {
      setSaveError(true);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div>
      <PageHeader
        className="mb-6"
        title="Dashboard"
        subtitle={
          customising
            ? "Drag blocks to rearrange them, show or hide them and set their width. To pin a page, drag it from the left menu onto Pinned shortcuts. Press Done to save."
            : "Your morning view of the business: what needs doing today, and where things stand."
        }
        actions={
          layout !== null ? (
            customising ? (
              <>
                {saveError && (
                  <span role="alert" className="text-xs text-ele-danger">
                    Could not save the layout. Please try again.
                  </span>
                )}
                <ConfirmButton
                  onConfirm={() => void resetToDefault()}
                  label="Reset to default"
                  confirmLabel="Reset the dashboard?"
                  busy={false}
                  disabled={saving}
                  title="Remove the saved layout and go back to the standard dashboard"
                  className="rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong"
                />
                <Button size="sm" onClick={() => void saveDraft()} disabled={saving}>
                  {saving ? "Saving..." : "Done"}
                </Button>
              </>
            ) : (
              <Button variant="secondary" size="sm" onClick={startCustomising}>
                Customise dashboard
              </Button>
            )
          ) : undefined
        }
      />

      {/* The launchpad grid (Session ADC Loop 3): one column below lg (a sensible stack in the
          arranged order -- the mobile dashboard rework is a separate queued session), two
          columns from lg with each block spanning both ("full", the default) or one ("half",
          so two half blocks sit side by side). Row spacing comes from each block's own bottom
          margin (row-gap 0), so the all-full default is the pre-ADC stack exactly. */}
      {layout === null ? (
        <Loader shape="panel" />
      ) : customising && draft ? (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onBlockDragEnd}>
          <SortableContext items={draft.blocks.map((b) => b.key)} strategy={rectSortingStrategy}>
            <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
              {draft.blocks.map((b) => (
                <BlockFrame
                  key={b.key}
                  placement={b}
                  pins={draft.pins}
                  onToggleVisible={() => toggleBlockVisible(b.key)}
                  onToggleSpan={() => toggleBlockSpan(b.key)}
                  onAddPin={addPin}
                  onRemovePin={removePin}
                />
              ))}
            </div>
          </SortableContext>
        </DndContext>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 lg:grid-cols-2">
          {layout.blocks
            .filter((b) => b.visible)
            .map((b) => (
              <div key={b.key} className={b.span === "half" ? "min-w-0" : "min-w-0 lg:col-span-2"}>
                <DashboardBlock blockKey={b.key} pins={layout.pins} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

// One block in customise mode: a SORTABLE dashed frame carrying the block's plain-English
// name, its drag handle (the grip carries the sortable listeners, never the whole frame, so
// the controls stay plain clicks), its Hide/Show and width toggles and (for the
// pinned-shortcuts block) the pin editor, over an inert preview of the block itself. The
// preview is `inert` so nothing inside it can be clicked or tabbed to while arranging --
// customise mode arranges blocks, it never operates them. The frame takes the block's real
// grid span so the arrangement previews true widths while dragging.
function BlockFrame({
  placement,
  pins,
  onToggleVisible,
  onToggleSpan,
  onAddPin,
  onRemovePin,
}: {
  placement: DashboardBlockPlacement;
  pins: string[];
  onToggleVisible: () => void;
  onToggleSpan: () => void;
  onAddPin: (to: string) => void;
  onRemovePin: (to: string) => void;
}): ReactNode {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: placement.key });
  const style = { transform: CSS.Transform.toString(transform), transition };
  const def = DASHBOARD_BLOCKS.find((d) => d.key === placement.key);
  if (!def) return null;
  // Session AFK: the hidden-block outline's faint white wash became the hairline token (0.12 to
  // 0.10 alpha, imperceptible on the dark ground) so it does not vanish into the light theme's
  // white surface. The visible-block purple is a brand wash and is unchanged.
  return (
    <section
      ref={setNodeRef}
      style={style}
      aria-label={`${def.label} block`}
      className={`mb-4 min-w-0 rounded-ele border border-dashed p-3 ${
        placement.visible ? "border-[rgba(125,33,199,0.35)]" : "border-[var(--ele-hairline)]"
      } ${placement.span === "half" ? "" : "lg:col-span-2"} ${isDragging ? "z-10 opacity-80" : ""}`}
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <button
          type="button"
          {...attributes}
          {...listeners}
          data-testid={`block-drag-handle-${placement.key}`}
          aria-label={`Drag to move the ${def.label} block`}
          title="Drag to move"
          className="flex h-6 w-5 flex-none cursor-grab touch-none items-center justify-center rounded text-ele-body opacity-50 transition hover:opacity-100 active:cursor-grabbing"
        >
          <svg aria-hidden viewBox="0 0 10 16" className="h-3.5 w-3.5 fill-current">
            <circle cx="3" cy="3" r="1.3" />
            <circle cx="7" cy="3" r="1.3" />
            <circle cx="3" cy="8" r="1.3" />
            <circle cx="7" cy="8" r="1.3" />
            <circle cx="3" cy="13" r="1.3" />
            <circle cx="7" cy="13" r="1.3" />
          </svg>
        </button>
        <span className="font-heading text-sm text-ele-strong">{def.label}</span>
        <span className="hidden text-xs text-ele-body sm:inline">{def.hint}</span>
        {!placement.visible && <StatusPill word="Hidden" tone="grey" />}
        <button
          type="button"
          onClick={onToggleSpan}
          title={
            placement.span === "full"
              ? "Switch to half width, so two half-width blocks can sit side by side"
              : "Switch back to full width"
          }
          className="ml-auto rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong hover:bg-ele-inset"
        >
          Width: {placement.span === "full" ? "Full" : "Half"}
        </button>
        <button
          type="button"
          onClick={onToggleVisible}
          className="rounded-ele border border-ele-purple-accent px-3 py-1 font-menu text-xs tracking-wide text-ele-body transition hover:text-ele-strong hover:bg-ele-inset"
        >
          {placement.visible ? "Hide" : "Show"}
        </button>
      </div>
      {placement.key === "pinned-shortcuts" ? (
        <PinEditor pins={pins} onAddPin={onAddPin} onRemovePin={onRemovePin} />
      ) : (
        <div inert className={`select-none ${placement.visible ? "" : "opacity-35"}`}>
          <DashboardBlock blockKey={placement.key} pins={pins} />
        </div>
      )}
    </section>
  );
}

// The pin editor (customise mode only): the current pins as removable chips plus a grouped
// picker mirroring the left menu's sections. Adding or removing a pin edits the draft; nothing
// persists until Done.
//
// Session AEA: the editor is also the DROP ZONE for the cross-surface pin drag. Drag a page
// from the left menu into this zone to pin it (live feedback names the page, and a duplicate or
// unpinnable page is refused WITH the reason -- never a silent no-op); drag a chip off the zone
// (anywhere else on the screen, the sidebar included) to unpin it. The dropdown and the chip X
// stay as the keyboard/touch path; drag and picker edit the same draft.
function PinEditor({
  pins,
  onAddPin,
  onRemovePin,
}: {
  pins: string[];
  onAddPin: (to: string) => void;
  onRemovePin: (to: string) => void;
}): ReactNode {
  const pinned = pins.map(navItemByPath).filter((i): i is NonNullable<ReturnType<typeof navItemByPath>> => i !== null);
  // Live feedback while a drag hovers the zone: ok = green "drop to pin", not ok = amber reason.
  const [hover, setHover] = useState<{ ok: boolean; message: string } | null>(null);
  const zoneRef = useRef<HTMLDivElement | null>(null);

  // While a CHIP is being dragged, everywhere OUTSIDE this zone is a valid drop surface that
  // unpins (that is the "drag it off the block" gesture; the sidebar is covered by this too).
  // Dropping the chip back inside the zone keeps it. Document-level because the drop can land
  // on any element; dragend clears the hover state and the shared drag record even when the
  // drag is cancelled (Escape) or dropped somewhere inert.
  useEffect(() => {
    function onDocDragOver(e: DragEvent) {
      if (currentPinDrag()?.kind !== "unpin") return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
    }
    function onDocDrop(e: DragEvent) {
      const drag = currentPinDrag();
      if (drag?.kind !== "unpin") return;
      if (zoneRef.current && e.target instanceof Node && zoneRef.current.contains(e.target)) return; // back on the block = keep
      e.preventDefault();
      endPinDrag();
      onRemovePin(drag.to);
    }
    function onDocDragEnd() {
      setHover(null);
      endPinDrag();
    }
    document.addEventListener("dragover", onDocDragOver);
    document.addEventListener("drop", onDocDrop);
    document.addEventListener("dragend", onDocDragEnd);
    return () => {
      document.removeEventListener("dragover", onDocDragOver);
      document.removeEventListener("drop", onDocDrop);
      document.removeEventListener("dragend", onDocDragEnd);
    };
  }, [onRemovePin]);

  function onZoneDragOver(e: ReactDragEvent) {
    const drag = currentPinDrag();
    const ours = drag !== null || Array.from(e.dataTransfer.types).includes(PIN_DRAG_TYPE);
    if (!ours) return; // a file or text selection: leave the browser default alone
    e.preventDefault();
    if (!drag) return;
    if (drag.kind === "unpin") {
      e.dataTransfer.dropEffect = "move";
      // "On the page", not "anywhere": a release past the window edge cancels the drag and the
      // pin survives (review finding; the copy must not promise more than the drop can do).
      setHover({ ok: true, message: "Drop it anywhere else on the page to unpin it." });
      return;
    }
    const decision = pinDropDecision(drag.to, pins);
    e.dataTransfer.dropEffect = decision.ok ? "copy" : "none";
    setHover(decision.ok ? { ok: true, message: `Drop to pin ${decision.label}.` } : { ok: false, message: decision.message });
  }

  function onZoneDrop(e: ReactDragEvent) {
    const to = e.dataTransfer.getData(PIN_DRAG_TYPE);
    const drag = currentPinDrag();
    setHover(null);
    endPinDrag();
    if (drag?.kind === "unpin") {
      e.preventDefault(); // dropped back on the block: keep the pin
      return;
    }
    const path = drag?.to ?? to;
    if (!path) return;
    e.preventDefault();
    const decision = pinDropDecision(path, pins);
    if (decision.ok) onAddPin(decision.to);
  }

  return (
    <div
      ref={zoneRef}
      data-testid="pin-drop-zone"
      onDragOver={onZoneDragOver}
      onDragLeave={(e) => {
        // A null relatedTarget is AMBIGUOUS (WebKit leaves it null moving between the zone's own
        // children; every engine leaves it null exiting the window), so keep the hover state and
        // let the document dragend listener clear it -- clearing here made Safari flicker the
        // feedback on every child crossing (review finding). A real move to another element
        // clears immediately.
        if (e.relatedTarget instanceof Node && !zoneRef.current?.contains(e.relatedTarget)) setHover(null);
      }}
      onDrop={onZoneDrop}
      className={`rounded-ele border border-dashed p-3 transition ${
        hover
          ? hover.ok
            ? "border-ele-green bg-[rgba(99,196,31,0.06)]"
            : "border-[var(--ele-amber-text-deep)] bg-[rgba(184,134,11,0.06)]"
          : "border-[var(--ele-hairline)]"
      }`}
    >
      <p className={`mb-2 text-xs ${hover ? (hover.ok ? "text-ele-green" : "text-amber-400") : "text-ele-body"}`}>
        {hover ? hover.message : "Drag a page here from the left menu to pin it, or pick one below."}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {pinned.map((item) => (
          <span
            key={item.to}
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(PIN_DRAG_TYPE, item.to);
              e.dataTransfer.effectAllowed = "move";
              beginPinDrag({ kind: "unpin", to: item.to });
            }}
            onDragEnd={() => endPinDrag()}
            title={`Drag off this block to unpin ${item.label}`}
            className="ele-qa ele-qa--purple cursor-grab active:cursor-grabbing"
          >
            {item.label}
            <button
              type="button"
              onClick={() => onRemovePin(item.to)}
              aria-label={`Unpin ${item.label}`}
              title={`Unpin ${item.label}`}
              className="ml-1 font-semibold transition hover:text-ele-strong"
            >
              ×
            </button>
          </span>
        ))}
        {pinned.length === 0 && <span className="text-xs text-ele-body">Nothing pinned yet.</span>}
        <select
          aria-label="Pin a page from the left menu"
          value=""
          onChange={(e) => {
            if (e.target.value) onAddPin(e.target.value);
          }}
          className="rounded-ele border border-ele-purple-accent bg-ele-field px-2 py-1.5 text-xs text-ele-body"
        >
          <option value="">Pin a page...</option>
          {pinnableNavSections().map((section) => (
            <optgroup key={section.label} label={section.label}>
              {section.items.map((item) => (
                <option key={item.to} value={item.to} disabled={pins.includes(item.to)}>
                  {item.label}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      </div>
    </div>
  );
}

// Renders one registered block by key. Each block is self-contained (its own fetch, loading and
// empty states), so the layout renderer stays a dumb, ordered list. An unknown key renders
// nothing (drift safety belt; applyDashboardLayout already drops unknown keys).
function DashboardBlock({ blockKey, pins }: { blockKey: DashboardBlockKey; pins: string[] }): ReactNode {
  switch (blockKey) {
    case "quick-actions":
      return <QuickActions />;
    case "pinned-shortcuts":
      return <PinnedShortcuts pins={pins} />;
    case "kpi-tiles":
      // SC7B: only the figures that need attention now; every figure is on Priorities ("All figures").
      return <KpiTileSet mode="attention" />;
    case "outstanding-jobs":
      return <OutstandingJobsPanel />;
    case "active-quotes":
      return <ActiveQuotesPanel />;
    case "standby":
      return <StandbyPanel />;
    case "office-notices":
      return <OfficeNoticesPanel />;
    default:
      return null;
  }
}

// Pinned shortcuts (Session ADC): the office's own shortcut buttons to left-menu pages, pinned
// in customise mode. Purple = navigation intent (the ACG colour logic); each button links
// straight to its page. The pins arrive pre-filtered against the live nav
// (applyDashboardLayout), and navItemByPath is the safety belt for a route retired after the
// layout was saved.
//
// Session AEA (1b) SUPERSEDES the ADC render-nothing-when-empty decision: a feature that leaves
// no trace when empty is a feature nobody discovers (the Director never found it). The empty
// state is ONE quiet dashed line naming the block and how to use it -- plain text, no second
// "Customise dashboard" control (the real button sits at the top of the same screen, and a
// duplicate-named control would be an accessibility smell). It disappears the moment anything
// is pinned, and the block can still be hidden entirely in customise mode.
function PinnedShortcuts({ pins }: { pins: string[] }): ReactNode {
  const items = pins.map(navItemByPath).filter((i): i is NonNullable<ReturnType<typeof navItemByPath>> => i !== null);
  if (items.length === 0) {
    return (
      <section className="mb-6">
        <h2 className="ele-section-title mb-2">Shortcuts</h2>
        <p className="rounded-ele border border-dashed border-[var(--ele-hairline)] px-3 py-2.5 text-xs text-ele-body">
          Pin your most-used pages here: press Customise dashboard, then drag a page across from the left menu or use
          the picker.
        </p>
      </section>
    );
  }
  return (
    <section className="mb-6">
      <h2 className="ele-section-title mb-2">Shortcuts</h2>
      <div className="flex flex-wrap items-center gap-2.5">
        {items.map((item) => (
          <Link key={item.to} to={item.to} title={item.hint ?? item.label} className="ele-qa ele-qa--purple max-sm:min-h-11">
            {item.label}
          </Link>
        ))}
      </div>
    </section>
  );
}

// The "Hot prospects" block (Session Z, Item 3) was retired in Session AKF (18 Aug 2026, Director
// decision): the Prospects feature it linked into is retired from ELE's nav (confirmed dead, zero
// real usage, script-imported leads only), so its only action -- a link to a prospect's detail
// page -- would 404. api.prospects.hot() and the Prospect model are untouched server-side; this
// was the widget's only caller.

// The daily actions (Session ACY set, Director-approved; Session AEA added "Send RFID tag"). Session SC7B (the calm screens,
// part 2) cut the row to FOUR, the ones the day starts with: Log call-out, Raise a quote, Open Mail, Release and send. The
// other five (Log phone call, Raise repair quote, Raise modernisation quote, Add a service, Send RFID tag) are the same
// buttons with the same modals, one press further in the shared overflow menu. An entrapment is a toggle INSIDE the Log
// call-out flow (see CalloutsPage), so "Log call-out" (?new=log) covers a routine call-out AND an entrapment. "Log phone
// call" opens the Session ABT LogCallModal (a cold call or a customer call). "Raise a quote" is the maintenance/ESA
// calculator; the repair and modernisation quotes open the projects NewProjectModal preset to that work type. "Add a
// service" opens the Session AR manual/backdated service-record modal (POST /api/jobsheets/manual). "Release and send" is
// the Awaiting release tab of Servicing (one press from here; it was two through the menu).
function QuickActions(): ReactNode {
  const navigate = useNavigate();
  // Customers are loaded once, when the call action is first opened, for the caller picker.
  const [callOpen, setCallOpen] = useState(false);
  const [addServiceOpen, setAddServiceOpen] = useState(false);
  // "Send RFID tag" (Session AEA): a picker over the register, each row carrying the real
  // RfidTagButton -- two clicks from the dashboard to the existing send flow, nothing forked.
  const [rfidOpen, setRfidOpen] = useState(false);
  // Which project quotation the NewProjectModal is open on (null = closed). The dashboard opens the
  // modal directly (routes are statically imported, so there is no chunk-splitting cost) preset to
  // the chosen work type; on create it navigates to the new project.
  const [projectPreset, setProjectPreset] = useState<WorksClassification | null>(null);
  // The caller picker inside the Log-a-call modal is a typeahead (Session SC2A): it asks for matches as the office
  // types, so the dashboard never loads the customer register.
  // Session ARM (D88): an unmatched call no longer makes a hidden sales lead; it waits on the
  // Enquiries page. This quick action is the one live place an unknown caller is logged, and it used
  // to just close, so the office was never told where the call had gone. Now it says so, with a link.
  const [enquiryNotice, setEnquiryNotice] = useState<string | null>(null);
  return (
    <section className="mb-6">
      <h2 className="ele-section-title mb-2">Quick actions</h2>
      {/* Session ACW Loop 6: flex-wrap lets the ghost actions fall onto a second line on a narrow
          phone rather than squeezing or overflowing; max-sm:min-h-11 gives each a >= 44px touch
          target below sm without touching a single pixel of the desktop row (.ele-qa is
          display:inline-flex + align-items:center already, so the taller box centres its label for
          free). No coloured left-edge accent stripes (the .ele-qa colour is a full 4-side border,
          not a banned side stripe); the separators are neutral hairlines. */}
      <div className="flex flex-wrap items-center gap-2.5">
        <Link to="/office/callouts?new=log" className="ele-qa ele-qa--amber max-sm:min-h-11">
          + Log call-out
        </Link>
        <Link to="/office/quotes/new" className="ele-qa ele-qa--green max-sm:min-h-11">
          + Raise a quote
        </Link>
        <Link to="/office/mail" className="ele-qa ele-qa--blue max-sm:min-h-11">
          Open Mail
        </Link>
        <Link to="/office/jobsheets/release" className="ele-qa ele-qa--purple max-sm:min-h-11">
          Release and send
        </Link>
        {/* Everything else the dashboard used to show as a button is one press further, in the same menu the rest of
            the platform uses for secondary actions (SC7B, the calm screen rule). Nothing was removed. */}
        <OverflowMenu label="More quick actions" closeOnSelect>
          <button type="button" role="menuitem" onClick={() => setCallOpen(true)} className={overflowItemCls}>
            Log phone call
          </button>
          <button type="button" role="menuitem" onClick={() => setProjectPreset("repair")} className={overflowItemCls}>
            Raise repair quote
          </button>
          <button type="button" role="menuitem" onClick={() => setProjectPreset("modernisation")} className={overflowItemCls}>
            Raise modernisation quote
          </button>
          <button type="button" role="menuitem" onClick={() => setAddServiceOpen(true)} className={overflowItemCls}>
            Add a service
          </button>
          <button type="button" role="menuitem" onClick={() => setRfidOpen(true)} className={overflowItemCls}>
            Send RFID tag
          </button>
        </OverflowMenu>
      </div>
      {enquiryNotice && (
        <p role="status" className="mt-3 flex flex-wrap items-center gap-x-2 rounded-ele border border-ele-green bg-ele-field-inset px-3 py-2 text-sm text-ele-strong">
          <span>{enquiryNotice}</span>
          <Link to="/office/mail?tab=calls" className="text-ele-green underline-offset-2 hover:underline">
            Open Calls in Mail
          </Link>
          <button type="button" onClick={() => setEnquiryNotice(null)} className="ml-auto font-menu text-xs tracking-wide text-ele-body hover:text-ele-strong">
            Dismiss
          </button>
        </p>
      )}
      <LogCallModal
        open={callOpen}
        onClose={() => setCallOpen(false)}
        onSaved={(created) => {
          setCallOpen(false);
          setEnquiryNotice(
            created && !created.customerId
              ? `Call from ${created.callerName?.trim() || created.callerPhone?.trim() || "the caller"} logged. It is on the Calls tab in Mail until you match it to a customer, raise work from it or delete it.`
              : null,
          );
        }}
      />
      {addServiceOpen && <AddServiceRecordModal onClose={() => setAddServiceOpen(false)} onCreated={() => undefined} />}
      {rfidOpen && <SendRfidTagModal onClose={() => setRfidOpen(false)} />}
      {projectPreset && (
        <NewProjectModal
          presetClassification={projectPreset}
          onClose={() => setProjectPreset(null)}
          onCreated={(id) => {
            setProjectPreset(null);
            navigate(`/office/projects/${id}`);
          }}
        />
      )}
    </section>
  );
}

// NOTE (Session AF, Item 1): the dashboard AI "Ask" box was removed from the layout because it
// answered off stale data. The natural-language query feature still exists server-side
// (POST /api/ai/query); it can return to the dashboard once the data layer it queries is reliable.
// Clean space beats a broken widget.

